import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import styled from 'styled-components';
import { Link } from 'react-router-dom';

// ─── Constants ───────────────────────────────────────────────────────────────

const CW = 480;   // canvas width
const CH = 580;   // canvas height

const A_COLS = 10;
const A_ROWS = 5;
const A_W    = 26;
const A_H    = 18;
const A_GX   = 16;   // horizontal gap
const A_GY   = 18;   // vertical gap
const A_STEP = 18;   // step-down amount

const P_W     = 44;
const P_H     = 22;
const P_Y     = CH - 58;
const P_SPEED = 220;   // px / sec

const PB_SPEED = 460;  // player bullet speed (px/sec, upward)
const AB_SPEED = 150;  // alien bullet speed (px/sec, downward)

const ROW_SCORE = [30, 30, 20, 20, 10];
const ROW_COLOR = ['#ff6b9d', '#ff9f43', '#ffd32a', '#0be881', '#67e8f9'];

// ─── Pure helpers ─────────────────────────────────────────────────────────────

function makeAliens() {
  const totalW = A_COLS * (A_W + A_GX) - A_GX;
  const startX = (CW - totalW) / 2;
  const startY = 70;
  const aliens = [];
  for (let r = 0; r < A_ROWS; r++) {
    for (let c = 0; c < A_COLS; c++) {
      aliens.push({
        col: c, row: r,
        x: startX + c * (A_W + A_GX),
        y: startY + r * (A_H + A_GY),
        alive: true,
      });
    }
  }
  return aliens;
}

function makeState(level = 1) {
  const speedBonus = (level - 1) * 80;
  return {
    player:        { x: CW / 2 - P_W / 2 },
    aliens:        makeAliens(),
    pBullet:       null,
    aBullets:      [],
    dir:           1,
    moveTimer:     0,
    moveInterval:  Math.max(120, 700 - speedBonus),
    shootTimer:    900,
    shootInterval: Math.max(600, 1200 - speedBonus),
    killed:        0,
    score:         0,
    lives:         3,
    invincible:    0,
    animFrame:     0,
    animTimer:     0,
    level,
    over:          false,
  };
}

// ─── Canvas drawing ───────────────────────────────────────────────────────────

function drawAlien(ctx, x, y, row, frame) {
  ctx.fillStyle = ROW_COLOR[row];
  if (row < 2) {
    ctx.fillRect(x + 4, y + 4, A_W - 8, A_H - 8);
    ctx.fillRect(x + 8, y + 1, A_W - 16, 5);
    if (frame === 0) {
      ctx.fillRect(x + 2, y - 3, 4, 4);
      ctx.fillRect(x + A_W - 6, y - 3, 4, 4);
      ctx.fillRect(x - 2, y + 6, 5, 5);
      ctx.fillRect(x + A_W - 3, y + 6, 5, 5);
    } else {
      ctx.fillRect(x + 4, y - 3, 3, 3);
      ctx.fillRect(x + A_W - 7, y - 3, 3, 3);
      ctx.fillRect(x, y + 8, 4, 4);
      ctx.fillRect(x + A_W - 4, y + 8, 4, 4);
    }
    ctx.fillRect(x + 4, y + A_H - 3, 4, 4);
    ctx.fillRect(x + A_W - 8, y + A_H - 3, 4, 4);
  } else if (row < 4) {
    ctx.fillRect(x + 2, y + 2, A_W - 4, A_H - 4);
    ctx.fillRect(x + 6, y, 4, 4);
    ctx.fillRect(x + A_W - 10, y, 4, 4);
    if (frame === 0) {
      ctx.fillRect(x - 3, y + 5, 5, 6);
      ctx.fillRect(x + A_W - 2, y + 5, 5, 6);
    } else {
      ctx.fillRect(x - 1, y + 9, 4, 4);
      ctx.fillRect(x + A_W - 3, y + 9, 4, 4);
    }
    ctx.fillRect(x + 2, y + A_H - 1, 4, 4);
    ctx.fillRect(x + A_W / 2 - 2, y + A_H - 1, 4, 4);
    ctx.fillRect(x + A_W - 6, y + A_H - 1, 4, 4);
  } else {
    ctx.fillRect(x + 3, y + 1, A_W - 6, A_H - 2);
    if (frame === 0) {
      ctx.fillRect(x - 1, y + 6, 5, 8);
      ctx.fillRect(x + A_W - 4, y + 6, 5, 8);
      ctx.fillRect(x + 8,  y + A_H - 2, 4, 5);
      ctx.fillRect(x + A_W - 12, y + A_H - 2, 4, 5);
    } else {
      ctx.fillRect(x + 1, y + 10, 4, 6);
      ctx.fillRect(x + A_W - 5, y + 10, 4, 6);
      ctx.fillRect(x + 4,  y + A_H - 1, 4, 4);
      ctx.fillRect(x + A_W - 8, y + A_H - 1, 4, 4);
    }
  }
}

function drawPlayer(ctx, x, color, flashing) {
  if (flashing) return;
  ctx.fillStyle = color;
  ctx.fillRect(x + 10, P_Y + 9, P_W - 20, P_H - 9);
  ctx.fillRect(x + P_W / 2 - 3, P_Y + 1, 6, 10);
  ctx.fillRect(x, P_Y + 15, 14, P_H - 15);
  ctx.fillRect(x + P_W - 14, P_Y + 15, 14, P_H - 15);
}

function drawMiniShip(ctx, x, y, color) {
  ctx.fillStyle = color;
  ctx.fillRect(x + 5, y + 5, 14, 8);
  ctx.fillRect(x + 10, y + 1, 4, 6);
  ctx.fillRect(x, y + 9, 6, 4);
  ctx.fillRect(x + 18, y + 9, 6, 4);
}

function drawScene(canvas, state, keysHeld, hl, bg, g2) {
  const ctx = canvas.getContext('2d');

  ctx.fillStyle = bg;
  ctx.fillRect(0, 0, CW, CH);

  // Starfield (static seed based on position)
  ctx.fillStyle = g2;
  ctx.globalAlpha = 0.5;
  for (let i = 0; i < 60; i++) {
    const sx = ((i * 97 + 13) % CW);
    const sy = ((i * 137 + 41) % (CH - 60));
    ctx.fillRect(sx, sy, i % 3 === 0 ? 2 : 1, i % 3 === 0 ? 2 : 1);
  }
  ctx.globalAlpha = 1;

  // HUD
  ctx.fillStyle = hl;
  ctx.font = 'bold 13px monospace';
  ctx.fillText(`SCORE  ${String(state.score).padStart(5, '0')}`, 16, 28);
  ctx.fillText(`LEVEL  ${state.level}`, CW / 2 - 36, 28);

  // Lives as mini ships
  for (let i = 0; i < state.lives; i++) {
    drawMiniShip(ctx, CW - 30 - i * 30, 14, hl);
  }

  // HUD separator
  ctx.fillStyle = g2;
  ctx.fillRect(0, 40, CW, 1);

  // Bottom barrier line
  ctx.fillStyle = g2;
  ctx.fillRect(0, CH - 32, CW, 1);

  // Aliens
  state.aliens.forEach(a => {
    if (!a.alive) return;
    drawAlien(ctx, a.x, a.y, a.row, state.animFrame);
  });

  // Player bullet
  if (state.pBullet) {
    ctx.fillStyle = hl;
    ctx.fillRect(state.pBullet.x - 2, state.pBullet.y, 4, 14);
  }

  // Alien bullets
  state.aBullets.forEach(b => {
    ctx.fillStyle = '#f65e3b';
    ctx.fillRect(b.x - 2, b.y, 4, 12);
  });

  // Player
  const flashing = state.invincible > 0 && Math.floor(state.invincible / 100) % 2 === 0;
  drawPlayer(ctx, state.player.x, hl, flashing);
}

// ─── Styles ──────────────────────────────────────────────────────────────────

const PageStyles = styled.div`
  padding: 16rem 0 10rem;
  min-height: 80vh;
`;

const BackLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.6rem;
  font-size: 1.4rem;
  color: var(--highlight-blue);
  opacity: 0.7;
  margin-bottom: 3rem;
  transition: opacity 0.2s ease;
  &:hover { opacity: 1; }
  &::before { content: '←'; }
`;

const GameWrapper = styled.div`
  max-width: 540px;
  margin: 0 auto;
  padding: 0 2rem;
  user-select: none;
`;

const TopBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1.6rem;
  flex-wrap: wrap;
  gap: 1rem;

  .game__title {
    font-size: 3.6rem;
    font-family: 'Montserrat Bold';
    color: var(--gray-1);
    line-height: 1;
  }
`;

const ScoreGroup = styled.div`
  display: flex;
  gap: 1rem;
`;

const ScoreBox = styled.div`
  background: var(--deep-dark);
  border: 1px solid var(--gray-2);
  border-radius: 8px;
  padding: 0.6rem 1.4rem;
  text-align: center;
  min-width: 80px;

  .label {
    font-size: 1rem;
    text-transform: uppercase;
    letter-spacing: 0.1rem;
    color: var(--gray-1);
    opacity: 0.45;
  }
  .value {
    font-size: 2rem;
    font-family: 'Montserrat Bold';
    color: var(--highlight-blue);
    line-height: 1.2;
  }
`;

const Controls = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1.2rem;
  gap: 1rem;

  .hint {
    font-size: 1.2rem;
    color: var(--gray-1);
    opacity: 0.35;
  }
`;

const NewGameBtn = styled.button`
  font-size: 1.3rem;
  padding: 0.6em 1.8em;
  background: transparent;
  color: var(--highlight-blue);
  border: 2px solid var(--highlight-blue);
  border-radius: 8px;
  cursor: pointer;
  font-family: 'RobotoMono Regular';
  transition: background 0.2s ease, color 0.2s ease;
  white-space: nowrap;
  &:hover { background: var(--highlight-blue); color: var(--dark-bg); }
`;

const BoardWrapper = styled.div`
  position: relative;
  border-radius: 12px;
  overflow: hidden;
  border: 2px solid var(--gray-2);
  line-height: 0;

  canvas {
    width: 100%;
    height: auto;
    display: block;
  }
`;

const Overlay = styled.div`
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.78);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1.2rem;
  z-index: 10;

  h2 {
    font-size: 3rem;
    font-family: 'Montserrat Bold';
    color: ${({ $color }) => $color || 'var(--gray-1)'};
  }
  p {
    font-size: 1.4rem;
    color: var(--gray-1);
    opacity: 0.6;
    text-align: center;
    padding: 0 2rem;
    line-height: 1.6;
  }
`;

const OBtn = styled.button`
  font-size: 1.5rem;
  padding: 0.75em 2.4em;
  border-radius: 8px;
  cursor: pointer;
  font-family: 'RobotoMono Regular';
  background: var(--highlight-blue);
  color: var(--dark-bg);
  border: none;
  transition: opacity 0.2s ease;
  &:hover { opacity: 0.85; }
`;

const Legend = styled.div`
  margin-top: 1.2rem;
  display: flex;
  gap: 2rem;
  flex-wrap: wrap;
  justify-content: center;

  .legend__item {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-size: 1.1rem;
    color: var(--gray-1);
    opacity: 0.45;
  }
  .legend__dot {
    width: 12px;
    height: 8px;
    border-radius: 2px;
  }
`;

// ─── Component ───────────────────────────────────────────────────────────────

export default function SpaceInvaders() {
  const canvasRef  = useRef(null);
  const stateRef   = useRef(null);
  const rafRef     = useRef(null);
  const lastTime   = useRef(null);
  const keysHeld   = useRef(new Set());
  const touchStart = useRef(null);
  const updateFn   = useRef(null);

  const [score, setScore]   = useState(0);
  const [best,  setBest]    = useState(() => Number(localStorage.getItem('space-best') || 0));
  const [phase, setPhase]   = useState('idle'); // idle | playing | over | levelup

  const getColors = () => {
    const cs = getComputedStyle(document.documentElement);
    return {
      hl: cs.getPropertyValue('--highlight-blue').trim() || '#4a9eff',
      bg: cs.getPropertyValue('--deep-dark').trim()      || '#0d0d14',
      g2: cs.getPropertyValue('--gray-2').trim()         || '#363636',
    };
  };

  // ── Draw ────────────────────────────────────────────────────────────────────
  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    const s = stateRef.current;
    if (!canvas || !s) return;
    const { hl, bg, g2 } = getColors();
    drawScene(canvas, s, keysHeld, hl, bg, g2);
  }, []);

  // ── Bottom-most alive alien per column (for shooting) ─────────────────────
  function getFrontLine(aliens) {
    const front = {};
    aliens.forEach(a => {
      if (!a.alive) return;
      if (front[a.col] === undefined || a.row > front[a.col].row) {
        front[a.col] = a;
      }
    });
    return Object.values(front);
  }

  // ── Update (stored in ref so RAF always uses latest) ──────────────────────
  useEffect(() => {
    updateFn.current = (dt) => {
      const s = stateRef.current;
      if (!s || s.over) return;

      const alive = s.aliens.filter(a => a.alive);
      if (alive.length === 0) {
        s.over = true;
        setPhase('levelup');
        return;
      }

      // Invincibility countdown
      if (s.invincible > 0) s.invincible = Math.max(0, s.invincible - dt);

      // Alien animation toggle
      s.animTimer += dt;
      if (s.animTimer >= 300) { s.animFrame ^= 1; s.animTimer = 0; }

      // Player movement (smooth, key-held)
      if (keysHeld.current.has('ArrowLeft') || keysHeld.current.has('a')) {
        s.player.x = Math.max(0, s.player.x - P_SPEED * dt / 1000);
      }
      if (keysHeld.current.has('ArrowRight') || keysHeld.current.has('d')) {
        s.player.x = Math.min(CW - P_W, s.player.x + P_SPEED * dt / 1000);
      }

      // Player bullet movement
      if (s.pBullet) {
        s.pBullet.y -= PB_SPEED * dt / 1000;
        if (s.pBullet.y < 40) {
          s.pBullet = null;
        } else {
          // Check collision with aliens
          for (const a of s.aliens) {
            if (!a.alive) continue;
            if (
              s.pBullet.x >= a.x - 2 && s.pBullet.x <= a.x + A_W + 2 &&
              s.pBullet.y >= a.y - 4 && s.pBullet.y <= a.y + A_H + 4
            ) {
              a.alive = false;
              s.pBullet = null;
              s.killed++;
              s.score += ROW_SCORE[a.row];
              setScore(s.score);
              setBest(b => {
                const nb = Math.max(b, s.score);
                if (nb > b) localStorage.setItem('space-best', String(nb));
                return nb;
              });
              // Speed up aliens as they die
              s.moveInterval = Math.max(80, s.moveInterval - Math.floor(600 / (A_COLS * A_ROWS)));
              break;
            }
          }
        }
      }

      // Alien movement timer
      s.moveTimer += dt;
      if (s.moveTimer >= s.moveInterval) {
        s.moveTimer = 0;
        const aliveNow = s.aliens.filter(a => a.alive);
        const rightEdge = Math.max(...aliveNow.map(a => a.x + A_W));
        const leftEdge  = Math.min(...aliveNow.map(a => a.x));

        if ((s.dir === 1 && rightEdge >= CW - 8) || (s.dir === -1 && leftEdge <= 8)) {
          // Step down and reverse
          s.aliens.forEach(a => { if (a.alive) a.y += A_STEP; });
          s.dir *= -1;
        } else {
          const stepX = 10 + s.level * 2;
          s.aliens.forEach(a => { if (a.alive) a.x += s.dir * stepX; });
        }

        // Check if any alien reached the player line
        if (s.aliens.some(a => a.alive && a.y + A_H >= P_Y - 10)) {
          s.over = true;
          setPhase('over');
          return;
        }
      }

      // Alien shooting
      s.shootTimer -= dt;
      if (s.shootTimer <= 0) {
        s.shootTimer = s.shootInterval + Math.random() * 400;
        const front = getFrontLine(s.aliens);
        if (front.length > 0) {
          const shooter = front[Math.floor(Math.random() * front.length)];
          s.aBullets.push({ x: shooter.x + A_W / 2, y: shooter.y + A_H });
        }
      }

      // Alien bullet movement + collision
      s.aBullets = s.aBullets.filter(b => {
        b.y += AB_SPEED * dt / 1000;
        if (b.y > CH) return false;

        // Hit player
        if (
          s.invincible <= 0 &&
          b.x >= s.player.x && b.x <= s.player.x + P_W &&
          b.y >= P_Y && b.y <= P_Y + P_H
        ) {
          s.lives--;
          s.invincible = 2000;
          s.aBullets = [];
          if (s.lives <= 0) {
            s.over = true;
            setPhase('over');
          }
          return false;
        }
        return true;
      });
    };
  });

  // ── Game loop ───────────────────────────────────────────────────────────────
  const loop = useCallback((ts) => {
    if (!lastTime.current) lastTime.current = ts;
    const dt = Math.min(ts - lastTime.current, 50); // cap at 50ms to avoid big jumps
    lastTime.current = ts;
    updateFn.current?.(dt);
    draw();
    rafRef.current = requestAnimationFrame(loop);
  }, [draw]);

  // ── Start / Restart ─────────────────────────────────────────────────────────
  const startGame = useCallback((level = 1) => {
    cancelAnimationFrame(rafRef.current);
    lastTime.current = null;
    stateRef.current = makeState(level);
    setScore(0);
    setPhase('playing');
    rafRef.current = requestAnimationFrame(loop);
  }, [loop]);

  useEffect(() => () => cancelAnimationFrame(rafRef.current), []);

  // Initial canvas draw
  useEffect(() => { draw(); }, [draw]);

  // ── Keyboard ────────────────────────────────────────────────────────────────
  useEffect(() => {
    const onDown = (e) => {
      keysHeld.current.add(e.key);
      if (['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', ' '].includes(e.key)) {
        e.preventDefault();
      }
      if ((e.key === ' ' || e.key === 'Enter') && phase !== 'playing') {
        startGame(); return;
      }
      if (e.key === ' ' && phase === 'playing') {
        const s = stateRef.current;
        if (s && !s.pBullet && !s.over) {
          s.pBullet = { x: s.player.x + P_W / 2, y: P_Y };
        }
      }
    };
    const onUp = (e) => keysHeld.current.delete(e.key);
    window.addEventListener('keydown', onDown);
    window.addEventListener('keyup',   onUp);
    return () => {
      window.removeEventListener('keydown', onDown);
      window.removeEventListener('keyup',   onUp);
    };
  }, [phase, startGame]);

  // ── Touch ────────────────────────────────────────────────────────────────────
  const onTouchStart = (e) => {
    touchStart.current = { x: e.touches[0].clientX, y: e.touches[0].clientY, t: Date.now() };
  };

  const onTouchEnd = (e) => {
    if (!touchStart.current) return;
    const dx  = e.changedTouches[0].clientX - touchStart.current.x;
    const dt2 = Date.now() - touchStart.current.t;
    touchStart.current = null;

    if (phase !== 'playing') { startGame(); return; }
    const s = stateRef.current;
    if (!s || s.over) return;

    if (Math.abs(dx) < 15 && dt2 < 250) {
      // Tap → shoot
      if (!s.pBullet) s.pBullet = { x: s.player.x + P_W / 2, y: P_Y };
    } else if (dx < -20) {
      keysHeld.current.add('ArrowLeft');
      setTimeout(() => keysHeld.current.delete('ArrowLeft'), 300);
    } else if (dx > 20) {
      keysHeld.current.add('ArrowRight');
      setTimeout(() => keysHeld.current.delete('ArrowRight'), 300);
    }
  };

  const currentLevel = stateRef.current?.level ?? 1;

  return (
    <PageStyles>
      <Helmet><title>Space Invaders | Anas Qazi</title></Helmet>
      <div className="container">
        <BackLink to="/games">All Games</BackLink>
      </div>

      <GameWrapper>
        <TopBar>
          <span className="game__title">👾 Space Invaders</span>
          <ScoreGroup>
            <ScoreBox>
              <div className="label">Score</div>
              <div className="value">{score}</div>
            </ScoreBox>
            <ScoreBox>
              <div className="label">Best</div>
              <div className="value">{best}</div>
            </ScoreBox>
          </ScoreGroup>
        </TopBar>

        <Controls>
          <span className="hint">← → / A D to move · Space to shoot</span>
          <NewGameBtn onClick={() => startGame(1)}>New Game</NewGameBtn>
        </Controls>

        <BoardWrapper onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
          <canvas ref={canvasRef} width={CW} height={CH} />

          {phase === 'idle' && (
            <Overlay>
              <h2>👾 Space Invaders</h2>
              <p>
                Destroy all alien invaders before<br />
                they reach your ship. Don't get hit.
              </p>
              <OBtn onClick={() => startGame(1)}>Start Game</OBtn>
            </Overlay>
          )}

          {phase === 'over' && (
            <Overlay $color="#f65e3b">
              <h2>Game Over</h2>
              <p>Score: {score}</p>
              <OBtn onClick={() => startGame(1)}>Try Again</OBtn>
            </Overlay>
          )}

          {phase === 'levelup' && (
            <Overlay $color="#ffd32a">
              <h2>Level {currentLevel} Clear!</h2>
              <p>Score: {score}<br />Aliens are faster next wave.</p>
              <OBtn onClick={() => startGame(currentLevel + 1)}>Next Wave</OBtn>
            </Overlay>
          )}
        </BoardWrapper>

        <Legend>
          {ROW_COLOR.slice(0, 3).map((c, i) => (
            <div key={i} className="legend__item">
              <div className="legend__dot" style={{ background: c }} />
              {i === 0 ? '30 pts' : i === 1 ? '20 pts' : '10 pts'}
            </div>
          ))}
        </Legend>
      </GameWrapper>
    </PageStyles>
  );
}
